import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { getStorageObjectUrl, supabase } from '../../core/supabase/supabase.client';

export interface Rider {
  id: string;
  status: string;
  user_id: string;
  store_id: number;
  code: string;
  verification_status: 'pending' | 'approved' | 'rejected' | 'PENDING' | 'APPROVED' | 'REJECTED';
  created_at: string;
  updated_at: string;
  full_name: string;
  vehicle_type: string;
  profile_photo_file_id: number;
  profile_photo_url?: string;
  onboarding_step: string;
  review_remark?: string;

  // Relations
  wallet?: RiderWallet;
  presence?: RiderPresence;
  documents?: RiderDocument[];
  bank_account?: RiderBankAccount;
  
  // Aggregates for UI
  orders?: number;
  rating?: number;
}

export interface RiderWallet {
  rider_id: string;
  balance: number;
  pending_balance: number;
  lifetime_earning: number;
  updated_at: string;
}

export interface RiderPresence {
  rider_id: string;
  is_online: boolean;
  status: string;
  latitude: number | null;
  longitude: number | null;
  last_seen_at: string;
  updated_at: string;
}

export interface RiderDocument {
  id: number;
  rider_id: string;
  doc_type: string;
  doc_number: string;
  file_id: number;
  status: string;
  remarks: string;
  verified_by: string;
  verified_at: string;
  created_at: string;
  updated_at: string;
  expiry_date: string;
  
  // Relation
  files?: {
    bucket: string;
    path: string;
  }
  url?: string;
}

export interface RiderBankAccount {
  rider_id: string;
  account_holder_name: string;
  account_number: string;
  ifsc: string;
  upi_id: string;
  updated_at: string;
}

export interface RiderPayout {
  id: number;
  rider_id: string;
  amount: number;
  status: 'PENDING' | 'PROCESSING' | 'PAID' | string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
  paid_at?: string;
  remarks?: string;
  rider?: {
    full_name: string;
  };
}

export interface RiderPage {
  data: Rider[];
  total: number;
  hasMore: boolean;
  stats: { total: number; online: number; pending: number };
}

@Injectable({ providedIn: 'root' })
export class RiderService {
  private table = 'riders';

  getFinanceRiders(): Observable<Rider[]> {
    return from(supabase.rpc('admin_finance_riders').then(({data,error})=>{
      if(error) throw error;
      return (data ?? []) as Rider[];
    }));
  }

  getAssignableRiders(storeId?: number): Observable<Rider[]> {
    let query = supabase
      .from(this.table)
      .select(`
        id,
        user_id,
        store_id,
        code,
        full_name,
        vehicle_type,
        status,
        verification_status,
        onboarding_step,
        created_at,
        updated_at,
        presence:rider_presence(
          is_online,
          status,
          latitude,
          longitude,
          last_seen_at,
          updated_at
        )
      `)
      .eq('verification_status', 'approved')
      .order('full_name', { ascending: true })
      .limit(500);

    if (storeId) {
      query = query.eq('store_id', storeId);
    }

    return from(
      query.then(({ data, error }) => {
        if (error) throw error;
        return (data || []) as unknown as Rider[];
      })
    );
  }

  getAllRiders(): Observable<Rider[]> {
    const query = supabase
      .from(this.table)
      .select(`
        *,
        wallet:rider_wallet(*),
        presence:rider_presence(*),
        bank_account:rider_bank_accounts(*),
        profile_photo:files!riders_profile_photo_file_id_fkey(*)
      `);

    return from(
      query.then(async ({ data, error }) => {
        if (error) throw error;

        return Promise.all(
          (data || []).map(async (r: any) => ({
            ...r,
            profile_photo_url: r.profile_photo
              ? await getStorageObjectUrl(r.profile_photo.bucket, r.profile_photo.path)
              : null,
            orders: Number(r.total_orders ?? r.completed_orders ?? 0),
            rating: Number(r.rating ?? 0),
          })),
        ) as Promise<Rider[]>;
      })
    );
  }

  getRidersPage(params: {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
    storeId?: number;
  } = {}): Observable<RiderPage> {
    const page = Math.max(params.page || 1, 1);
    const pageSize = Math.min(Math.max(params.pageSize || 25, 1), 100);

    return from(
      supabase.rpc('admin_list_riders_v2', {
        p_store_id: params.storeId ?? null,
        p_status: params.status && params.status !== 'All' ? params.status.toLowerCase() : null,
        p_search: params.search?.trim() || null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      }).then(async ({ data, error }) => {
        if (error) throw error;
        const payload = data as unknown as {
          data?: (Rider & { profile_bucket?: string; profile_path?: string })[];
          total?: number;
          has_more?: boolean;
          stats?: { total?: number; online?: number; pending?: number };
        };
        const riders = await Promise.all(
          (payload.data || []).map(async (rider) => ({
            ...rider,
            profile_photo_url: rider.profile_bucket && rider.profile_path
              ? await getStorageObjectUrl(rider.profile_bucket, rider.profile_path)
              : undefined,
            orders: Number(rider.orders || 0),
            rating: Number(rider.rating || 0),
          })),
        );

        return {
          data: riders,
          total: Number(payload.total || 0),
          hasMore: Boolean(payload.has_more),
          stats: {
            total: Number(payload.stats?.total || 0),
            online: Number(payload.stats?.online || 0),
            pending: Number(payload.stats?.pending || 0),
          },
        };
      })
    );
  }

  getRiderDocuments(riderId: string): Observable<RiderDocument[]> {
    return from(
      supabase
        .from('rider_documents')
        .select(`
          *,
          files:files(*)
        `)
        .eq('rider_id', riderId)
        .then(async ({ data, error }) => {
          if (error) throw error;

          return Promise.all(
            (data || []).map(async (doc: any) => ({
              ...doc,
              url: doc.files
                ? await getStorageObjectUrl(doc.files.bucket, doc.files.path)
                : null,
            })),
          ) as Promise<RiderDocument[]>;
        })
    );
  }

  updateRegistrationStatus(riderId: string, status: 'approved' | 'rejected', reason?: string) {
    const accountStatus = status === 'approved' ? 'active' : 'inactive';
    return from(
      supabase
        .from(this.table)
        .update({ 
          verification_status: status,
          status: accountStatus,
          review_remark: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', riderId)
    );
  }

  updateDocumentStatus(docId: number, status: 'approved' | 'rejected', remarks?: string) {
    return from(
      supabase
        .from('rider_documents')
        .update({ 
          status,
          remarks,
          verified_at: new Date().toISOString()
        })
        .eq('id', docId)
    );
  }

  updateRiderAvailability(riderId: string, isOnline: boolean) {
    return from(
      supabase
        .from('rider_presence')
        .upsert({ 
          rider_id: riderId, 
          is_online: isOnline,
          status: isOnline ? 'online' : 'offline',
          updated_at: new Date().toISOString()
        })
    );
  }

  getRiderPayouts(page = 1, pageSize = 10, status?: string, storeId?: number): Observable<{ data: RiderPayout[], count: number }> {
    let query = supabase
      .from('payout_requests')
      .select(storeId ? '*, rider:riders!inner(full_name)' : '*, rider:riders(full_name)', { count: 'exact' });

    if (status && status !== 'ALL') {
      query = query.ilike('status', status);
    }
    
    if (storeId) {
      query = query.eq('rider.store_id', storeId);
    }

    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    query = query.range(start, end).order('created_at', { ascending: false });

    return from(
      query.then(({ data, error, count }) => {
        if (error) throw error;
        
        return {
          data: (data || []).map((p: any) => ({
            ...p,
            rider: Array.isArray(p.rider) ? p.rider[0] : p.rider
          })) as RiderPayout[],
          count: count || 0
        };
      })
    );
  }

  getPayoutStats(storeId?: number): Observable<{ pending: number; processing: number; paid: number }> {
    let query = supabase
      .from('payout_requests')
      .select(storeId ? 'amount, status, rider:riders!inner(store_id)' : 'amount, status');

    if (storeId) {
      query = query.eq('rider.store_id', storeId);
    }

    return from(
      query.then(({ data, error }) => {
        if (error) throw error;
        const stats = { pending: 0, processing: 0, paid: 0 };
        (data as any[] || []).forEach(p => {
          const st = p.status?.toUpperCase();
          if (st === 'PENDING') stats.pending += Number(p.amount);
          if (st === 'PROCESSING') stats.processing += Number(p.amount);
          if (st === 'PAID') stats.paid += Number(p.amount);
        });
        return stats;
      })
    );
  }

  updatePayoutStatus(id: number, status: string, additionalData?: any) {
    return from(
      supabase
        .from('payout_requests')
        .update({
          status,
          ...additionalData
        })
        .eq('id', id)
    );
  }
}
